import { useInfiniteQuery } from "@tanstack/react-query";

import { api } from "@/api/api";
import { BASE_API_URL } from "@/api/api";
import type {
  HotelsSortValue,
  IHotelsFilters,
} from "@/features/hotels/types/types";
import type { FindHotelsOutputDto } from "@/types/codegen";

const PAGE_SIZE = 20;

type Params = {
  searchResultKey: string | undefined;
  searchResultToken: string | undefined;
  filters: IHotelsFilters | null;
  bookingNights?: number;
  shouldFetch: boolean;
  sortValue: HotelsSortValue;
  hotelName?: string | undefined;
  totalOffers?: number;
};

export const useHotelSearchResults = ({
  searchResultKey,
  searchResultToken,
  filters,
  shouldFetch,
  sortValue,
  hotelName,
  totalOffers,
}: Params) => {
  const params = filters
    ? {
        searchResultKey,
        searchResultToken,
        perPage: PAGE_SIZE,
        sortField: sortValue.field,
        sortOrder: sortValue.order,
        minPrice: filters.priceRange[0],
        maxPrice: filters.priceRange[1],
        stars: filters.starRating,
        boardBasis: filters.boardBasis,
        hotelName: (hotelName || "").length >= 2 ? hotelName : undefined,
      }
    : null;

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching, isError } =
    useInfiniteQuery({
      queryKey: ["hotels", "search", "results", params] as const,
      queryFn: ({ pageParam, signal }) =>
        api
          .get<FindHotelsOutputDto>(`${BASE_API_URL}/hotels`, {
            params: {
              ...params,
              page: pageParam,
            },
            signal,
          })
          .then((res) => res.data),
      initialPageParam: 1,
      getNextPageParam: (lastPage, pages) => {
        const totalFetched = pages.reduce(
          (sum, page) => sum + page.data.length,
          0,
        );

        if (totalOffers !== undefined && totalFetched >= totalOffers) {
          return undefined;
        }

        // Fallback if totalOffers is not provided
        return lastPage.data.length === PAGE_SIZE
          ? pages.length + 1
          : undefined;
      },
      enabled: Boolean(
        searchResultKey && searchResultToken && shouldFetch && filters,
      ),
    });

  const isEmpty = data?.pages.flatMap((page) => page.data).length === 0;
  const hasReachedEnd = !hasNextPage;

  return {
    data,
    isLoading,
    isFetching,
    isError,
    isEmpty,
    hasReachedEnd,
    fetchNextPage,
  };
};
